import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	BILL_PAYMENT_SENDING_WINDOW_MS,
	billPaymentAnswerSchema,
	billPaymentPath,
	isBillPaymentInFlight,
	type BillPaymentAnswer,
	type BillPaymentRequest,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

// A vendor's bill paid with Revolut: the draft payment the API keeps for it, which waits in Revolut for the business's
// owner to approve. Its key starts with "payments", Finance's own, so whatever asks Finance again asks for it too.

/**
 * Whether a vendor's bill can be paid with Revolut, to which vendors, and its draft payment — which the API asks
 * Revolut about when it was last checked over a minute ago; anyone who can read Finance. A document that is not a
 * vendor's bill of this year answers 404, which no retry changes.
 */
export const billPaymentQueryOptions = (fileId: string) =>
	queryOptions({
		queryKey: ["payments", "bill-payment", fileId],
		queryFn: ({ signal }) => apiRequest(billPaymentPath(fileId), billPaymentAnswerSchema, { signal }),
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
		// While a sending of its draft may still be answered, asked again every quarter of that minute, so the drawer
		// offers what may be done once it has passed.
		refetchInterval: query => {
			const payment = query.state.data?.billPayment;
			return payment !== undefined && payment !== null && isBillPaymentInFlight(payment, Date.now())
				? BILL_PAYMENT_SENDING_WINDOW_MS / 4
				: false;
		},
	});

/**
 * Sends a vendor's bill to Revolut as a draft payment, and answers where it stands. An editor of Finance; 409 when it
 * may not be sent, with why, and 502 when Revolut did not answer.
 */
export function sendBillPayment(request: BillPaymentRequest): Promise<BillPaymentAnswer> {
	return apiRequest(apiPaths.billPayments, billPaymentAnswerSchema, { method: "POST", body: request });
}

/**
 * Deletes a bill's draft payment that waits for approval in Revolut. An editor of Finance; 409 once it was acted on.
 */
export async function deleteBillPayment(fileId: string): Promise<void> {
	await apiRequest(billPaymentPath(fileId), z.undefined(), { method: "DELETE" });
}

/**
 * Forgets a bill's draft payment that Ravnar can no longer follow in Revolut: Ravnar's record of it is removed, without
 * a word to Revolut, which may still hold the draft. An editor of Finance; 409 for a draft Ravnar can still follow.
 */
export async function forgetBillPayment(fileId: string): Promise<void> {
	await apiRequest(billPaymentPath(fileId), z.undefined(), { method: "DELETE", query: { forget: "true" } });
}
